import { z } from 'zod';

// The fixed Logspot fields a CRM destination writes, in HubSpot property terms.
// One definition drives provisioning, the setup check, writes and the explainer.

export const CRM_PROPERTY_GROUP = { name: 'logspot', label: 'Logspot' } as const;

export const CRM_FIELD_KEYS = [
  'company_id',
  'signal_type',
  'signal_strength',
  'signal_at',
  'intent_summary',
  'source_url',
] as const;
export type CrmFieldKey = (typeof CRM_FIELD_KEYS)[number];

export type CrmFieldDefinition = {
  key: CrmFieldKey;
  // Default target: a Logspot-owned property in the Logspot group.
  property: string;
  label: string;
  description: string;
  type: 'string' | 'number' | 'datetime';
  fieldType: 'text' | 'textarea' | 'number' | 'date';
  // Lets a lost create be reconciled by upsert instead of creating a duplicate.
  unique?: boolean;
};

export const CRM_FIELDS: readonly CrmFieldDefinition[] = [
  {
    key: 'company_id',
    property: 'logspot_company_id',
    label: 'Logspot Company ID',
    description: 'The Logspot company this record is linked to.',
    type: 'string',
    fieldType: 'text',
    unique: true,
  },
  {
    key: 'signal_type',
    property: 'logspot_signal_type',
    label: 'Logspot Latest Signal',
    description: 'The most recent Logspot signal for this company.',
    type: 'string',
    fieldType: 'text',
  },
  {
    key: 'signal_strength',
    property: 'logspot_signal_strength',
    label: 'Logspot Signal Strength',
    description: 'Strength of the latest signal, from 1 (low) to 3 (high).',
    type: 'number',
    fieldType: 'number',
  },
  {
    key: 'signal_at',
    property: 'logspot_signal_at',
    label: 'Logspot Signal Time',
    description: 'When the latest signal happened.',
    type: 'datetime',
    fieldType: 'date',
  },
  {
    key: 'intent_summary',
    property: 'logspot_intent_summary',
    label: 'Logspot Intent Summary',
    description: 'A short summary of the latest signal.',
    type: 'string',
    fieldType: 'textarea',
  },
  {
    key: 'source_url',
    property: 'logspot_source_url',
    label: 'Logspot Link',
    description: 'Opens this account in Logspot.',
    type: 'string',
    fieldType: 'text',
  },
];

// Setup check result per field, shown in the hub before any write.
export const CRM_FIELD_STATES = [
  'ready',
  'missing',
  'archived',
  'incompatible',
  'read_only',
] as const;
export type CrmFieldState = (typeof CRM_FIELD_STATES)[number];

export const crmSetupCheckSchema = z.object({
  ready: z.boolean(),
  checked_at: z.iso.datetime(),
  fields: z.array(
    z.object({
      key: z.enum(CRM_FIELD_KEYS),
      property: z.string(),
      label: z.string(),
      state: z.enum(CRM_FIELD_STATES),
    }),
  ),
});
export type CrmSetupCheck = z.infer<typeof crmSetupCheckSchema>;

// Point fields at existing HubSpot properties; null restores the default.
export const crmFieldMapRequestSchema = z.object({
  field_map: z.partialRecord(z.enum(CRM_FIELD_KEYS), z.string().max(255).nullable()),
});
export type CrmFieldMapRequest = z.infer<typeof crmFieldMapRequestSchema>;

export type CrmPropertyOption = { name: string; label: string };

// The setup panel's data: the check plus the properties each field could use.
export type CrmSetupView = CrmSetupCheck & {
  options: Record<CrmFieldKey, CrmPropertyOption[]>;
  field_map: Partial<Record<CrmFieldKey, string>>;
  // Each field's Logspot-owned target in this CRM (names differ per CRM).
  default_targets: Record<CrmFieldKey, string>;
  // False when the CRM's fields come from elsewhere (Salesforce's package).
  can_create_fields: boolean;
};
